import React, { useCallback, useImperativeHandle, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import {
  Text,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Tooltip,
  IconButton,
  useColorModeValue,
  VStack,
  Center, Icon,
} from "@chakra-ui/react";
import { FaTrash, FaPrint, FaDownload } from "react-icons/fa";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { MAX_FILE_SIZE_BYTES } from "constants/documents";

export const FileAttachmentMultipleInput = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const showToast = useCustomToast();
  const imageBg = useColorModeValue("white", "navy.800");
  const imageBorder = useColorModeValue("gray.200", "gray.400");

  const {
    isDisabled = false,
    translationKey = "documents.form",
    nameVariable = [],
    acceptedTypes = ['*'],
    showClearButton = true,
    showPrintButton = true,
    showDownloadButton = true,
    onPrintFile,
    onDownloadFile,
    onDeleteFile,
    onClearAction = null,
    originalFileName = null,
    ...rest
  } = props;

  useEffect(() => {
    if (Array.isArray(nameVariable)) {
      const existingFiles = nameVariable.map(name => ({ name, existing: true }));
      setFiles(existingFiles);
    }
  }, [nameVariable]);

  const onError = useCallback(() => {
    showToast({ title: t("system.messages.errors.invalid-file"), status: "error" });
  }, [showToast, t]);

  const onDropAccepted = useCallback((acceptedFiles) => {
    const oversizedFiles = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      oversizedFiles.forEach(() => {
        showToast({
          title: t("system.messages.errors.max-size"),
          status: "error",
        });
      });
    }

    const validFiles = acceptedFiles.filter(file => file.size <= MAX_FILE_SIZE_BYTES);

    if (validFiles.length > 0) {
      setFiles(prev => {
        const existingNames = new Set(prev.map(f => f.name));
        const duplicates = validFiles.filter(file => existingNames.has(file.name));
        const uniqueFiles = validFiles.filter(file => !existingNames.has(file.name));

        if (duplicates.length > 0) {
          showToast({
            title: t("document-type.messages.errors.duplicated-type"),
            status: "warning",
          });
        }

        const newFiles = uniqueFiles.map(file => ({ file, name: file.name }));
        return [...prev, ...newFiles];
      });
    }
  }, [showToast, t]);

  const removeFile = useCallback((fileToRemove) => {
    setFiles(prev =>
        prev.filter(f =>
            !(f.name === fileToRemove.name && !!f.existing === !!fileToRemove.existing)
        )
    );
  }, []);

  const handlePrintFile = (f) => {
    if (f.file) {
      const url = URL.createObjectURL(f.file);
      const win = window.open(url);
      win?.print();
    } else {
      onPrintFile?.(f.name);
    }
  };

  const handleDownloadFile = (f) => {
    if (f.file) {
      const url = URL.createObjectURL(f.file);
      const link = document.createElement("a");
      link.href = url;
      link.download = f.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      onDownloadFile?.(f.name);
    }
  };

  const handleDeleteFile = (f) => {
    if (f.file) {
      removeFile(f);
    } else {
      onDeleteFile?.(f.name);
    }
  };
  const handleRemoveExistingFile = useCallback(async (fileToRemove) => {
    if (onClearAction && originalFileName === fileToRemove.name) {
      await onClearAction();
    }
    setFiles(prev =>
        prev.filter(f =>
            !(f.name === fileToRemove.name)
        )
    );
  }, [originalFileName]);

  useImperativeHandle(ref, () => ({
    getFiles: () => files.filter(f => !f.existing).map(f => f.file),
  }));

  return (
      <FormControl {...rest}>
        <FormLabel fontSize="sm" fontWeight="bold">
          {t(`${translationKey}.fields.attachments.label`)}
        </FormLabel>

        <Dropzone
            disabled={isDisabled}
            onDropAccepted={onDropAccepted}
            onDropRejected={onError}
            accept={acceptedTypes}
        >
          {({ getRootProps, getInputProps }) => (
              <Flex
                  direction="column"
                  border="2px dashed"
                  borderRadius="md"
                  bg={imageBg}
                  borderColor={imageBorder}
                  p={4}
                  minH="100px"
                  justify="center"
                  {...getRootProps()}
                  cursor={isDisabled ? "not-allowed" : "pointer"}
              >
                <Input {...getInputProps()} accept={acceptedTypes.join(',')} />

                <VStack align="stretch" spacing={2} w="100%">
                  {files.map((f, index) => (
                      <Flex key={index} justify="space-between" align="center" gap={2}>
                        <Text fontSize="sm" isTruncated flex="1">
                          {f.name}
                        </Text>
                        <Flex gap={1}>
                          {showPrintButton && (
                              <Tooltip label={t("system.actions.print")} hasArrow>
                                <IconButton
                                    size="xs"
                                    aria-label={t("system.actions.print")}
                                    icon={<FaPrint />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePrintFile(f);
                                    }}
                                />
                              </Tooltip>
                          )}
                          {showDownloadButton && (
                              <Tooltip label={t("system.actions.download")} hasArrow>
                                <IconButton
                                    size="xs"
                                    aria-label={t("system.actions.download")}
                                    icon={<FaDownload />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadFile(f);
                                    }}
                                />
                              </Tooltip>
                          )}
                          {showClearButton && (
                              <Tooltip hasArrow label={t("system.actions.clear")}>
                                <IconButton
                                    size="xs"
                                    colorScheme="red"
                                    aria-label={t("system.actions.clear")}
                                    icon={<FaTrash />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFile(f);
                                    }}
                                />
                              </Tooltip>
                          )}
                          {f.existing && onClearAction && originalFileName && (
                              <Tooltip hasArrow label={t("system.actions.clear")}>
                                <IconButton
                                    size="xs"
                                    colorScheme="red"
                                    aria-label={t("system.actions.clear")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveExistingFile(f);
                                    }}
                                    icon={<Icon as={FaTrash} w={3} h={3} />}
                                />
                              </Tooltip>
                          )}
                        </Flex>
                      </Flex>
                  ))}
                  <Center>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      {t("system.placeholders.file-dropzone")}
                    </Text>
                  </Center>
                </VStack>
              </Flex>
          )}
        </Dropzone>
      </FormControl>
  );
});
